// ABN Lookup Service for Australian Business Register
// Based on ABR Web Services: https://abr.business.gov.au/Documentation/WebServiceMethods



export interface ABNEntity {
  abn: string;
  entityStatus: string;
  entityType: string;
  gstStatus?: string;
  businessName?: string[];
  tradingName?: string[];
  legalName?: string;
  mainBusinessLocation?: {
    state: string;
    postcode: string;
  };
  score?: number;
}

export interface ABNSearchResponse {
  searchResultsList?: {
    searchResultsRecord?: ABNEntity[];
    numberOfRecords?: string;
    exceedsMaximum?: string;
  };
  exception?: {
    exceptionDescription: string;
    exceptionCode: string;
  };
}



// Note: You need to register for a free GUID at https://www.abr.business.gov.au/Tools/WebServices/
// For demonstration purposes, we'll use a placeholder GUID
const ABR_GUID = process.env.NEXT_PUBLIC_ABR_GUID || '46c31249-35b5-4604-83f9-e4bffb690170';

const ABR_BASE_URL = 'https://abr.business.gov.au/abrxmlsearch/AbrXmlSearch.asmx';

/**
 * Search ABN by name using our Next.js API route (which proxies to ABR API)
 * This avoids CORS issues by making the actual ABR API call from the server
 */
export async function searchABNByName(
  searchTerm: string
): Promise<ABNEntity[]> {
  try {
    // Only send the search query - all configuration happens server-side
    const params = new URLSearchParams({
      name: searchTerm
    });

    const response = await fetch(`/api/abn-lookup?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error(`ABN lookup failed: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      console.warn('ABN lookup returned error:', result.error);
      return [];
    }

    return result.data || [];
  } catch (error) {
    console.error('Error searching ABN:', error);
    return [];
  }
}

/**
 * Search ABN by ABN number
 */
export async function searchByABN(abn: string): Promise<ABNEntity | null> {
  if (!abn || !isValidABN(abn)) {
    throw new Error('Invalid ABN format');
  }

  try {
    const params = new URLSearchParams({
      searchString: abn,
      guid: ABR_GUID,
      includeHistoricalDetails: 'N'
    });

    const url = `${ABR_BASE_URL}/SearchByABNv202001?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/xml',
        'User-Agent': 'Next.js ABN Lookup Client'
      }
    });

    if (!response.ok) {
      throw new Error(`ABR API request failed: ${response.status} ${response.statusText}`);
    }

    const xmlText = await response.text();
    return parseABRXMLResponse(xmlText)[0] || null;
  } catch (error) {
    console.error('Error searching by ABN:', error);
    throw new Error('Failed to lookup ABN. Please try again.');
  }
}

/**
 * Validate ABN format (11 digits)
 */
export function isValidABN(abn: string): boolean {
  // Remove spaces and non-digits
  const cleanABN = abn.replace(/\s/g, '').replace(/\D/g, '');
  
  if (cleanABN.length !== 11) {
    return false;
  }

  // ABN checksum validation
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  
  for (let i = 0; i < 11; i++) {
    const digit = parseInt(cleanABN[i], 10);
    if (i === 0) {
      sum += (digit - 1) * weights[i];
    } else {
      sum += digit * weights[i];
    }
  }

  return sum % 89 === 0;
}

/**
 * Validate ACN format (9 digits)
 */
export function isValidACN(acn: string): boolean {
  // Remove spaces and non-digits
  const cleanACN = acn.replace(/\s/g, '').replace(/\D/g, '');
  return cleanACN.length === 9;
}

/**
 * Format ABN for display (XX XXX XXX XXX)
 */
export function formatABN(abn: string): string {
  const cleanABN = abn.replace(/\s/g, '').replace(/\D/g, '');
  if (cleanABN.length === 11) {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2, 5)} ${cleanABN.slice(5, 8)} ${cleanABN.slice(8, 11)}`;
  }
  return abn;
}

/**
 * Parse XML response from ABR API
 * This is a simplified parser - in production you might want to use a proper XML parser
 */
function parseABRXMLResponse(xmlText: string): ABNEntity[] {
  const entities: ABNEntity[] = [];

  try {
    // This is a simplified XML parsing approach
    // In production, you should use a proper XML parser like fast-xml-parser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    // Check for exceptions first
    const exception = xmlDoc.querySelector('exception');
    if (exception) {
      const exceptionDesc = exception.querySelector('exceptionDescription')?.textContent;
      throw new Error(exceptionDesc || 'Unknown ABR API error');
    }

    // Parse search results
    const searchRecords = xmlDoc.querySelectorAll('searchResultsRecord');
    
    searchRecords.forEach(record => {
      const abn = record.querySelector('ABN identifierValue')?.textContent || '';
      const entityStatus = record.querySelector('ABN identifierStatus')?.textContent || '';
      
      // Get business names
      const businessNames: string[] = [];
      const businessNameElements = record.querySelectorAll('businessName organisationName');
      businessNameElements.forEach(el => {
        const name = el.textContent?.trim();
        if (name) businessNames.push(name);
      });

      // Get trading names
      const tradingNames: string[] = [];
      const tradingNameElements = record.querySelectorAll('tradingName organisationName');
      tradingNameElements.forEach(el => {
        const name = el.textContent?.trim();
        if (name) tradingNames.push(name);
      });

      // Get legal name
      const legalNameElement = record.querySelector('legalName familyName, legalName givenName, legalName organisationName');
      const legalName = legalNameElement?.textContent?.trim() || '';

      // Get main business location
      const stateElement = record.querySelector('mainBusinessPhysicalAddress stateCode');
      const postcodeElement = record.querySelector('mainBusinessPhysicalAddress postcode');
      
      const mainBusinessLocation = {
        state: stateElement?.textContent || '',
        postcode: postcodeElement?.textContent || ''
      };

      // Get entity type
      const entityTypeElement = record.querySelector('entityType entityTypeCode');
      const entityType = entityTypeElement?.textContent || '';

      // Get GST status
      const gstStatusElement = record.querySelector('goodsAndServicesTax');
      const gstStatus = gstStatusElement?.textContent || '';

      if (abn) {
        entities.push({
          abn: formatABN(abn),
          entityStatus,
          entityType,
          gstStatus,
          businessName: businessNames.length > 0 ? businessNames : undefined,
          tradingName: tradingNames.length > 0 ? tradingNames : undefined,
          legalName: legalName || undefined,
          mainBusinessLocation
        });
      }
    });

    return entities;
  } catch (error) {
    console.error('Error parsing ABR XML response:', error);
    return [];
  }
}


